import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { PropertyListings } from '@/types/PropertyListings';
import { Badge } from '@/components/ui/badge';
import Helper from '@/lib/helper';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ChartLine, Edit, Trash, CirclePause, Handshake, Eye, MousePointerClick, MessageSquare, Sparkles } from 'lucide-react';
import { differenceInDays, differenceInHours, differenceInMinutes, format } from 'date-fns';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from '@/components/ui/checkbox';

interface _ActiveListingTable {
    loading: boolean;
    listings: PropertyListings[] | null;
    startIndex?: number;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedListingId: React.Dispatch<React.SetStateAction<number | null>>;
    setActionType: React.Dispatch<React.SetStateAction<'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost'>>;
    bulkMode: boolean;
    selectedIds: number[];
    onToggleSelect: (id: number) => void;
    onSelectAll: () => void;
    onBulkDeactivate: () => void;
}

const ActiveListingTable: React.FC<_ActiveListingTable> = ({
    loading,
    listings,
    startIndex = 0,
    setOpen,
    setSelectedListingId,
    setActionType,
    bulkMode,
    selectedIds,
    onToggleSelect,
    onSelectAll,
    onBulkDeactivate,
}) => {
    const allSelected = !!listings?.length && listings.every(l => selectedIds.includes(l.id))

    const getExpiryDate = (listing: PropertyListings) => {
        if (listing.expiryDate) return new Date(listing.expiryDate);
        const createdAt = listing.createdAt ? new Date(listing.createdAt) : new Date();
        return new Date(createdAt.getFullYear(), createdAt.getMonth() + 1, createdAt.getDate(), createdAt.getHours(), createdAt.getMinutes(), createdAt.getSeconds());
    };

    const getTimeLeft = (listing: PropertyListings) => {
        const now = new Date();
        const expiry = getExpiryDate(listing);
        const diffInMinutes = differenceInMinutes(expiry, now);
        if (diffInMinutes <= 0) return 'Expired';
        if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''}`;
        const diffInHours = differenceInHours(expiry, now);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''}`;
        const diffInDays = differenceInDays(expiry, now);
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''}`;
    };

    const triggerAction = (id: number, type: 'delete' | 'deactivate' | 'close' | 'reopen' | 'activate' | 'repost') => {
        setSelectedListingId(id);
        setActionType(type);
        setOpen(true);
    };

    return (
        <TooltipProvider>
            <Table className='mt-3'>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-[500px] pr-8'>
                            <div className='flex items-center gap-3'>
                                {bulkMode && (
                                    <Checkbox
                                        checked={allSelected}
                                        onCheckedChange={onSelectAll}
                                        aria-label='Select all listings'
                                    />
                                )}
                                Listing
                            </div>
                        </TableHead>
                        <TableHead className='px-6'>Expires</TableHead>
                        <TableHead className='px-6'>Status</TableHead>
                        <TableHead className='px-6'>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <div className='flex justify-center py-4'>
                                    <Spinner />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : listings && listings.length > 0 ? (
                        listings.map((listing, index) => (
                            <TableRow
                                key={listing.id}
                                className={bulkMode && selectedIds.includes(listing.id) ? 'bg-muted/50' : ''}
                            >
                                <TableCell className='max-w-[500px] w-[500px]'>
                                    <div className='flex items-center'>
                                        {/* Per-row checkbox beside image */}
                                        {bulkMode && (
                                            <Checkbox
                                                checked={selectedIds.includes(listing.id)}
                                                onCheckedChange={() => onToggleSelect(listing.id)}
                                                className='mr-3 flex-shrink-0'
                                                aria-label={`Select listing ${listing.listingReferenceNumber}`}
                                            />
                                        )}

                                        <span className='w-6 mr-2 flex-shrink-0 self-center text-right text-xs font-medium text-gray-400 tabular-nums'>
                                            {startIndex + index + 1}
                                        </span>
                                        <div
                                            className='rounded-lg w-[100px] h-[100px] flex-shrink-0'
                                            style={{
                                                background: `url(${import.meta.env.VITE_AWS_S3}/${
                                                    listing.medias.find(x => x.mediaType === 1 && 'isCover' in x && x.isCover === 1)?.path ||
                                                    listing.medias.find(x => x.mediaType === 1)?.path
                                                })`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center center',
                                            }}
                                        />
                                        <div className='px-3 min-w-0 flex-1'>
                                            <div className='break-words' style={{ whiteSpace: 'pre-line' }}>
                                                {listing.headline}
                                            </div>
                                            <div className='text-xs mt-1 text-gray-500 whitespace-break-spaces'>
                                                {listing.rawAddress}
                                            </div>
                                            <div className='font-bold mt-2'>
                                                {Helper.formatCurrency(Number(listing.price))}
                                            </div>
                                            <div className='flex items-center gap-2 mt-1.5'>
                                                <Badge variant={listing.forSaleOrRentId === 1 ? 'outline' : 'secondary'}>
                                                    {listing.forSaleOrRentId === 1 ? 'Sale' : 'Rent'}
                                                </Badge>
                                                {listing.createChannel === 'ai' && (
                                                    <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700' title='Created with AI'>
                                                        <Sparkles className='h-3 w-3' /> AI
                                                    </Badge>
                                                )}
                                                <span className='text-[11px] text-gray-700 font-mono bg-gray-100 px-1.5 py-0.5 rounded'>
                                                    {listing.listingReferenceNumber}
                                                </span>
                                            </div>
                                            <div className='flex items-center gap-4 mt-2 text-xs text-gray-500'>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className='flex items-center gap-1'>
                                                            <Eye className='h-3.5 w-3.5' />
                                                            {listing.stats?.impressions ?? 0}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Impressions</p></TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className='flex items-center gap-1'>
                                                            <MousePointerClick className='h-3.5 w-3.5' />
                                                            {listing.stats?.clicks ?? 0}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Clicks</p></TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className='flex items-center gap-1'>
                                                            <MessageSquare className='h-3.5 w-3.5' />
                                                            {listing.enquiries?.length ?? 0}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Enquiries</p></TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className='px-6'>
                                    <div className='text-sm font-medium'>{getTimeLeft(listing)} left</div>
                                    <div className='text-xs text-muted-foreground mt-1'>
                                        {format(getExpiryDate(listing), 'dd MMM yyyy')}
                                    </div>
                                </TableCell>
                                <TableCell className='px-6'>
                                    <Badge
                                    className={
                                        listing.isInternal === 1
                                        ? "bg-blue-100 border-blue-700 text-blue-700"
                                        : "bg-green-100 border-green-700 text-green-700"
                                    }
                                    >
                                    {listing.isInternal === 1 ? "Internal" : "Public"}
                                    </Badge>
                                </TableCell>
                                <TableCell className='px-6'>
                                    <div className='flex flex-nowrap items-center gap-2'>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button type='button' size='sm' variant='outline' asChild>
                                                    <Link to={`/listings/view/${listing.id}`}>
                                                        <ChartLine className='h-3.5 w-3.5' />
                                                        View
                                                    </Link>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent><p>View Analytics</p></TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button type='button' size='sm' variant='outline' asChild>
                                                    <Link to={`/listings/edit/${listing.id}`}>
                                                        <Edit className='h-3.5 w-3.5' />
                                                        Edit
                                                    </Link>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Edit Listing</p></TooltipContent>
                                        </Tooltip>
                                        {listing.deactivated !== 1 && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type='button' size='sm' variant='outline'
                                                        onClick={() => triggerAction(listing.id, 'deactivate')}
                                                    >
                                                        <CirclePause className='h-3.5 w-3.5' />
                                                        Pause
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>Deactivate Listing</p></TooltipContent>
                                            </Tooltip>
                                        )}
                                        {listing.closed !== 1 && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type='button' size='sm' variant='outline'
                                                        className='text-gray-600 hover:text-gray-800'
                                                        onClick={() => triggerAction(listing.id, 'close')}
                                                    >
                                                        <Handshake className='h-3.5 w-3.5' />
                                                        Closed
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>Done deal</p></TooltipContent>
                                            </Tooltip>
                                        )}
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type='button' size='sm' variant='destructive'
                                                    onClick={() => triggerAction(listing.id, 'delete')}
                                                >
                                                    <Trash className='h-3.5 w-3.5' />
                                                    Delete
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Delete Listing</p></TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} className="text-center py-4">
                                No listings found.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TooltipProvider>
    );
};

export default ActiveListingTable;
